import * as React from "react";
import { observer } from "mobx-react";
import { UitkFigure, UitkFigureAspectRatioType } from "uitk-react-images";
import { UitkCard, UitkCardContentSection, UitkCardLink } from "uitk-react-cards";
import { DestinationSuggestionsProps } from "../typings";
import {
  Destination,
  DestinationSuggestionsFlexModuleResult,
} from "src/typings/microserviceModels/destination-suggestions-flex-module.d.ts";
import { ItemKeyHelper } from "components/utility/ItemKeyHelper";
import { IsomorphicCarousel } from "components/shared/IsomorphicCarousel/IsomorphicCarousel";
import { Action } from "components/utility/analytics/FlexAnalyticsUtils";
import { TrackedLink } from "components/utility/analytics/TrackedLink";
import { useLocalization } from "@shared-ui/localization-context";
import { withMabTracker, MabTrackEvent } from "components/utility/analytics/MabTracker";
import { UitkHeading, UitkSubheading } from "uitk-react-text";
import { UitkSpacing } from "uitk-react-spacing";
import { withStores } from "stores";
import { BlossomImage } from "src/components/shared/BlossomImage/BlossomImage";

const EmbeddingRedesignCard = withMabTracker(
  ({
    destination,
    cardIndex,
    mabTrack,
  }: {
    destination: Destination;
    cardIndex: number;
    mabTrack?: (x: MabTrackEvent) => null;
  }) => {
    const rfrrId = cardIndex + 1;

    const cardClicked = mabTrack
      ? () => {
          const mabTrackData: MabTrackEvent = {
            moduleName: "embedding",
            eventTarget: "embedding",
            eventType: "homepageClick.nonPath",
            rfrr: rfrrId.toString(),
          };
          mabTrack(mabTrackData);
        }
      : undefined;

    return (
      <>
        <UitkCard className="EmbeddingCard uitk-flex-justify-content-flex-start uitk-flex-column">
          <UitkCardContentSection padded={false}>
            <UitkFigure ratio={UitkFigureAspectRatioType.R16_9} className="uitk-card-roundcorner-all">
              <BlossomImage src={destination.destinationImg} alt={destination.imgAlt} />
            </UitkFigure>
            <UitkCardLink>
              <TrackedLink
                moduleName="Embedding"
                action={Action.CLICK}
                href={destination.url}
                rfrr={rfrrId}
                onClick={cardClicked}
              >
                {destination.name}
              </TrackedLink>
            </UitkCardLink>
          </UitkCardContentSection>
          <UitkSpacing padding={{ blockstart: "one" }}>
            <UitkHeading tag="h3" size={6}>
              {destination.name}
            </UitkHeading>
          </UitkSpacing>
        </UitkCard>
      </>
    );
  }
);

export const EmbeddingRedesign = withStores("flexModuleModelStore")(
  observer((props: DestinationSuggestionsProps) => {
    const { id, options } = props.flexTemplateNode;
    const model = props.flexModuleModelStore.getModel(id) as DestinationSuggestionsFlexModuleResult | null;

    /* istanbul ignore next */
    if (!model) {
      return null;
    }

    const keyHelper = new ItemKeyHelper(id);
    const { formatText } = useLocalization();
    const title = formatText("destinationSuggestions.embedding.title.v2");
    const subtitle = formatText("destinationSuggestions.embedding.title");

    return (
      <UitkSpacing padding={{ blockend: "three" }}>
        <div data-testid="embeddingRedesign" id={id} data-fm={options.fmId}>
          <UitkSpacing padding={{ blockend: "three", blockstart: "three" }}>
            <div>
              <UitkHeading tag="h2" size={3}>
                {title}
              </UitkHeading>
              <UitkSubheading tag="h3">{subtitle}</UitkSubheading>
            </div>
          </UitkSpacing>
          <IsomorphicCarousel
            className="carouselComponent"
            itemsVisible={{
              lg: 4,
              md: 4,
              sm: 2,
            }}
            buttonText={{
              nextButton: formatText("carousel.item.next"),
              prevButton: formatText("carousel.item.prev"),
            }}
            pageBy={1}
            peekWidth={1}
          >
            {model.destinations.map((destination, index) => {
              return (
                <div key={keyHelper.next()}>
                  <EmbeddingRedesignCard destination={destination} cardIndex={index} mabTrack={props.mabTrack} />
                </div>
              );
            })}
          </IsomorphicCarousel>
        </div>
      </UitkSpacing>
    );
  })
);
(EmbeddingRedesign as React.SFC).displayName = "EmbeddingRedesignView";
